/**
 * Tree-level Legacy Book HTML preview — Stage 3B.
 * Route: /tree/:treeId/legacy-book-preview
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getApiBase } from '../utils/apiBase';
import { humanizeError } from '../utils/errorHandling';
import { treeHref } from '../utils/prettyArchiveLinks';
import { TreeLegacyBookPrintLayout } from '../components/legacy-book/TreeLegacyBookPrintLayout';
import { LoadingState } from '../components/ui/LoadingState';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { Button } from '../components/ui/button';
import '../components/legacy-book/legacy-book-preview.css';

const API = getApiBase();

export default function TreeLegacyBookPreviewPage() {
  const { treeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!treeId) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/trees/${treeId}/legacy-book-data`);
        if (!cancelled) setData(res.data);
      } catch (error) {
        if (!cancelled) {
          toast.error(humanizeError(error));
          navigate('/dashboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [treeId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5efe6]">
        <LoadingState message="Preparing your Legacy Book preview…" />
      </div>
    );
  }

  if (!data) return null;

  const backLink = treeHref({ id: data.tree?.id || treeId });
  const readinessTab = `${backLink}?tab=readiness`;

  return (
    <div
      className="legacy-book-preview-root tree-legacy-book-preview-root"
      data-theme="heritage"
      data-testid="tree-legacy-book-preview-page"
    >
      <header className="legacy-book-no-print sticky top-0 z-20 border-b border-[#e8e2d8]/80 bg-[#fffdf8]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={readinessTab}
              className="inline-flex items-center gap-1.5 text-xs text-[#6c6258] hover:text-[#2c2824]"
              data-testid="tree-legacy-book-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Legacy Book
            </Link>
            <span className="hidden text-[#e8e2d8] sm:inline" aria-hidden>
              |
            </span>
            <div className="hidden min-w-0 sm:block">
              <BrandWordmark className="text-sm" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full text-xs"
              onClick={() => window.print()}
              data-testid="tree-legacy-book-print"
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Print preview
            </Button>
            <BookOpen className="h-4 w-4 shrink-0 text-[#5c4a32]" aria-hidden />
            <span className="truncate font-serif text-sm">Keepsake preview</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="legacy-book-no-print mb-6 max-w-2xl">
          <h1 className="font-serif text-2xl font-light text-[#1c1917] sm:text-3xl">Preview keepsake book</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#6c6258]">
            A calm keepsake preview of your family archive — stories, photographs and loved ones gathered for future
            generations. Your existing PDF download remains available from the Legacy Book tab.
          </p>
        </div>

        <TreeLegacyBookPrintLayout data={data} />
      </div>

      {!user ? null : (
        <p className="legacy-book-no-print sr-only" aria-live="polite">
          Tree Legacy Book preview loaded for {data.tree?.name}
        </p>
      )}
    </div>
  );
}
